import React, { useCallback, useMemo, useRef } from "react";
import FilterAssistant from "../filterAssistant/FilterAssistant";
import { executeTableCommand } from "../filterAssistant/capabilities/tableView/tableExecutor";
import { executeNavigation } from "../filterAssistant/capabilities/dashboardNavigation/navExecutor";
import { buildNavigationTargets } from "../filterAssistant/capabilities/dashboardNavigation/navCatalog";
import { buildDashboardFilterParams } from "../filterAssistant/capabilities/dashboardFilters/applyMapping";
import { buildActiveFilterState } from "../filterAssistant/capabilities/dashboardFilters/activeFilters";
import { emitClientSpans, makeSpan } from "../filterAssistant/core/telemetry";
import { useOttoFilterFields } from "../dashboard/useOttoFilterFields";
import { getCachedProject } from "../../utils/dashboardSession";
import { getCapitalRequestLabel } from "../../utils/capitalRequestLabel";

// Mounts Otto on the Recommendations page. Two capabilities are wired:
//  - table_view: sort / show-hide columns / pin / pagination / resets / export via the page's `tableController`.
//  - filters (LOCAL): the page shares the dashboard's filter contract but applies to the page's OWN filters
//    (`myFilters`, same MyFilters/submitMasterFilter path the native panel uses), NOT the global dashboard
//    filters. Uses the SHARED useOttoFilterFields hook so ALL bands (criticality/priority/dynamic) are
//    offered + applied, exactly like the dashboard. `buildDashboardFilterParams` maps the resolved payload to
//    the local filters; `onSeedLabels` seeds masterFilterList so the applied-filter TAGS render (Otto applies
//    ids the tag layer resolves lazily). The container provides the raw setters; this stays store-lean.
export default function RecommendationsOtto({ projectId, projectIds, tableController, myFilters, onSetLocalFilters, onSeedLabels }) {
    const { filterFields, filtersReady, fieldsMeta, buildingIds, singleProjectId, projectById } = useOttoFilterFields({
        projectId,
        projectIds,
        getCachedProject
    });
    const undoRef = useRef(undefined);

    // The permission-scoped reachable pages powering the `navigate` capability (and the page manifest). Without
    // this the navigate handler grounds against an EMPTY catalog, so EVERY "take me to …" (incl. "go to the
    // dashboard") fails to find a page. Built with the SAME builder + project context the dashboard mount uses
    // (AiFilterAssistant), so the FCA Dashboard is offered and the project sub-page breadcrumbs match on-screen
    // navigation. projectName drives the Recommendations/sub-page breadcrumb trail; capitalRequestLabel the
    // renamed Capital Request target — both sourced exactly like the dashboard mount.
    const projectName = useMemo(() => {
        if (singleProjectId == null) return "";
        const matches = String(projectById?.id) === String(singleProjectId);
        const cached = getCachedProject(singleProjectId);
        return (matches ? projectById?.name : null) || cached?.name || "";
    }, [projectById, singleProjectId]);
    const capitalRequestLabel = useMemo(() => {
        const matches = singleProjectId != null && String(projectById?.id) === String(singleProjectId);
        return getCapitalRequestLabel(matches ? projectById?.miscellaneous : null);
    }, [projectById, singleProjectId]);
    const navTargets = useMemo(
        () => buildNavigationTargets({ singleProjectId, projectIds, projectName, capitalRequestLabel }),
        [singleProjectId, projectIds, projectName, capitalRequestLabel]
    );

    const onTableApply = useCallback(changes => executeTableCommand(changes, tableController), [tableController]);

    const onApply = useCallback(
        (payload, trace) => {
            const current = myFilters || {};
            const { params, clearedAll, diagnostics, labelSeeds } = buildDashboardFilterParams({ payload, filterFields, current });
            // Seed masterFilterList/filterFields so the applied-filter tags (AppliedFilters "From Local")
            // resolve id -> name — Otto applies ids without opening each field's dropdown (same fix as the
            // dashboard's seedAiFilterLabels).
            if (labelSeeds && onSeedLabels && (labelSeeds.fields.length || Object.keys(labelSeeds.options).length)) {
                onSeedLabels(labelSeeds);
            }
            // Trace the apply — the last mile where a dropped paramKey silently no-ops. Mirrors the dashboard's
            // applyAiFilters span (status "error" on any drop) so a LOCAL Recommendations apply is just as
            // visible in the tracing waterfall as a dashboard one; best-effort (emitClientSpans never throws).
            if (trace && trace.requestId) {
                const dropped = (diagnostics && diagnostics.dropped) || [];
                emitClientSpans(trace.requestId, trace.sessionId, [
                    makeSpan("apply", {
                        input: { page: "recommendations" },
                        output: { ...(diagnostics || {}), clearedAll, finalParamKeys: Object.keys(params) },
                        status: dropped.length ? "error" : "ok",
                        metadata: { dropped: dropped.length, applied: ((diagnostics && diagnostics.applied) || []).length }
                    })
                ]);
            }
            undoRef.current = current; // snapshot for Undo
            if (onSetLocalFilters) onSetLocalFilters(params);
        },
        [myFilters, filterFields, onSeedLabels, onSetLocalFilters]
    );

    const onUndo = useCallback(() => {
        if (undoRef.current === undefined) return;
        const snap = undoRef.current;
        undoRef.current = undefined;
        if (onSetLocalFilters) onSetLocalFilters(snap || {});
    }, [onSetLocalFilters]);

    // The page's LOCAL filters in Otto's activeFilters shape (shared builder — same contract as the dashboard
    // mount), so multi-turn refinement, "remove the … filter", and "which filters are active?" all read the
    // current local state against the COMPLETE assembled field set.
    const activeFilters = useMemo(() => buildActiveFilterState(myFilters, filterFields), [myFilters, filterFields]);

    return (
        <FilterAssistant
            projectId={projectId}
            projectIds={projectIds}
            filterFields={filterFields}
            filtersReady={filtersReady}
            fieldsMeta={fieldsMeta}
            buildingIds={buildingIds}
            activeFilters={activeFilters}
            page="recommendations"
            navTargets={navTargets}
            tableController={tableController}
            onTableApply={onTableApply}
            onNavigate={executeNavigation}
            onApply={onSetLocalFilters ? onApply : undefined}
            onUndo={onUndo}
        />
    );
}
